/**
 * Crafting window (registry id `crafting`) on the Tome page (Tome UI Round 4): WindowHost draws
 * the frame, title and eyebrow (playerWindowPresentation.ts); this is the page — a recipe index by
 * profession, the selected recipe as a folio, then the one Craft action and the realm's answer.
 * The recipe list, the craft request and its pending/confirmed/refused result are useCrafting's
 * (server-authoritative); nothing here predicts an outcome.
 */
import React, { useMemo, useState } from 'react';
import type { CraftingRecipe } from '../../../../shared/CharacterTypes';
import { useCrafting } from '../../hooks/useCrafting';
import { useInventory } from '../../hooks/useInventory';
import { useGame } from '../../contexts/GameContext';
import { TomeButton } from '../../primitives/tome';
import { craftingItemName } from './InventoryPresentation';
import { CraftingRecipeFolio } from './CraftingRecipeFolio';
import { CraftingRecipeList, craftingRecipeState } from './CraftingRecipeList';
import './CraftingTome.css';

export const CraftingContent: React.FC = () => {
  const { recipes, lastResult, canCraft, craft, refresh, pendingRecipeId, recipesLoaded, connected } = useCrafting();
  const { items } = useInventory();
  const { player } = useGame();
  const [selectedId, setSelectedId] = useState<string>();
  const [query, setQuery] = useState('');
  const owned = useMemo(() => {
    const counts = new Map<string, number>();
    for (const item of items) counts.set(item.id, (counts.get(item.id) ?? 0) + item.quantity);
    return counts;
  }, [items]);
  const filtered = recipes.filter(recipe => `${recipe.name} ${craftingItemName(recipe.outputItemId)} ${recipe.profession}`
    .toLowerCase().includes(query.trim().toLowerCase()));
  const selected = filtered.find(recipe => recipe.id === selectedId) ?? (selectedId ? null : filtered[0]) ?? null;
  const level = typeof player?.level === 'number' && Number.isFinite(player.level) ? player.level : null;
  const stateOf = (recipe: CraftingRecipe) => craftingRecipeState(recipe, level, canCraft(recipe));
  const selectedState = selected ? stateOf(selected) : null;
  const levelMet = selectedState === 'ready' || selectedState === 'missing';
  const craftable = selected != null && canCraft(selected) && levelMet && connected && !pendingRecipeId;
  const viewer = player ? { level: player.level, characterClass: player.class } : undefined;
  const hint = pendingRecipeId ? 'The result will appear when the realm answers.' : !levelMet ? 'Meet the level requirement to craft.'
    : selected && !canCraft(selected) ? 'Gather the missing ingredients to craft.' : 'Ingredients and output change only after confirmation.';
  return <section className="crafting-tome" aria-label="Crafting recipes">
    <div className="crafting-tome__toolbar">
      <label className="crafting-tome__search">Find a recipe<input type="search" value={query}
        onChange={event => setQuery(event.target.value)} placeholder="Name, output or profession…" /></label>
      <TomeButton tone="secondary" className="crafting-tome__refresh" onClick={refresh}
        disabled={!!pendingRecipeId || !connected}>Refresh</TomeButton>
    </div>
    <div className="crafting-tome__body">
      <nav className="crafting-tome__list" aria-label="Recipe list">
        {!connected ? <p className="crafting-tome__note">Connect to a realm to load recipes.</p>
          : !recipesLoaded ? <p className="crafting-tome__note" role="status">Loading recipes…</p>
            : !filtered.length ? <p className="crafting-tome__note">{query ? 'No matching recipes.' : 'No recipes are available.'}</p>
              : <CraftingRecipeList recipes={filtered} selectedId={selected?.id} stateOf={stateOf} onSelect={setSelectedId} />}
      </nav>
      <section className="crafting-tome__detail" aria-label="Selected recipe">
        {selected && selectedState ? <CraftingRecipeFolio key={selected.id} recipe={selected} state={selectedState}
          owned={owned} viewer={viewer} />
          : <div className="crafting-tome__empty"><h3>Select a recipe</h3>
            <p>Inspect its output and required ingredients before crafting.</p></div>}
      </section>
    </div>
    {selected && <footer className="crafting-tome__action">
      <TomeButton tone="primary" fullWidth disabled={!craftable} onClick={() => craft(selected.id)}>
        {pendingRecipeId ? 'Waiting for confirmation…' : `Craft ${selected.name}`}</TomeButton>
      <small>{hint}</small>
    </footer>}
    {lastResult && <div role={lastResult.success ? 'status' : 'alert'} className="crafting-tome__result"
      data-craft-result={lastResult.status ?? (lastResult.success ? 'confirmed' : 'refused')}>{lastResult.message}</div>}
  </section>;
};
